import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Text, Badge, Flex, HStack, VStack, Image, useToast,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb,
} from '@chakra-ui/react';
import api from '../api';
import PageShell from './PageShell';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const fmtMoney = (v) => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function BusinessPipelineDashboard({ isOpen, onClose, fullPage, onBack }) {
  const active = fullPage || isOpen;
  const [stages, setStages] = useState([]);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(null);
  const toast = useToast();

  const fetchStages = useCallback(async () => {
    try { const { data } = await api.get('/prospects/pipeline-stages'); setStages(data); } catch(e) {}
  }, []);
  const fetchEntries = useCallback(async () => {
    try { const { data } = await api.get('/prospects/pipeline'); setEntries(data); } catch(e) {}
  }, []);

  useEffect(() => { if (active) { fetchStages(); fetchEntries(); } }, [active, fetchStages, fetchEntries]);

  const updateEntry = async (id, field, value) => {
    setSaving(id);
    try {
      await api.put(`/prospects/entries/${id}`, { [field]: value });
      await fetchEntries();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(null); }
  };

  // Group entries by stage
  const grouped = {};
  stages.forEach(s => { grouped[s.prospect_status] = { stage: s, items: [] }; });
  entries.forEach(e => {
    const key = e.prospect_status;
    if (grouped[key]) grouped[key].items.push(e);
    else {
      // Find closest matching stage
      const fallback = stages.find(s => s.prospect_status === 'new') || stages[0];
      if (fallback && grouped[fallback.prospect_status]) grouped[fallback.prospect_status].items.push(e);
    }
  });

  // Sort items within each stage by conversion_likelihood DESC
  Object.values(grouped).forEach(g => {
    g.items.sort((a, b) => (b.conversion_likelihood || 0) - (a.conversion_likelihood || 0));
  });

  const totalPipeline = entries.reduce((s, e) => s + parseFloat(e.calculated_opportunity_value || e.opportunity_value || 0), 0);

  const content = (
    <VStack spacing={3} align="stretch">
      {/* Summary bar */}
      <Flex justify="space-between" align="center" bg="gray.50" p={3} borderRadius="md">
        <HStack spacing={4}>
          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Total Pipeline</Text>
            <Text fontSize="22px" fontWeight="800" color="green.600">{fmtMoney(totalPipeline)}</Text></Box>
          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Prospects</Text>
            <Text fontSize="22px" fontWeight="800" color="blue.600">{entries.length}</Text></Box>
          <Box><Text fontSize="10px" color="gray.400" fontWeight="700" textTransform="uppercase">Stages</Text>
            <Text fontSize="22px" fontWeight="800" color="gray.600">{stages.length}</Text></Box>
        </HStack>
      </Flex>

      {/* Kanban columns */}
      <Flex gap={3} overflowX="auto" pb={2} minH="500px" align="start">
        {stages.map(stage => {
          const g = grouped[stage.prospect_status] || { items: [] };
          const stageTotal = g.items.reduce((s, e) => s + parseFloat(e.calculated_opportunity_value || e.opportunity_value || 0), 0);
          return (
            <Box key={stage.id} minW="260px" maxW="280px" flex="0 0 260px"
              bg="gray.50" borderRadius="lg" border="2px solid" borderColor={stage.color || '#CBD5E0'}>
              {/* Stage header */}
              <Box bg={stage.color || '#CBD5E0'} px={3} py={2} borderTopRadius="md">
                <Flex justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Text fontSize="13px" fontWeight="700" color="white">{stage.name}</Text>
                    <Badge bg="whiteAlpha.300" color="white" fontSize="10px" borderRadius="full" px={1.5}>{g.items.length}</Badge>
                  </HStack>
                </Flex>
                <Text fontSize="12px" fontWeight="600" color="whiteAlpha.900">{fmtMoney(stageTotal)}</Text>
              </Box>

              {/* Cards */}
              <VStack spacing={2} p={2} align="stretch" maxH="calc(100vh - 250px)" overflowY="auto">
                {g.items.length === 0 && (
                  <Text fontSize="10px" color="gray.400" textAlign="center" py={4}>No prospects</Text>
                )}
                {g.items.map(entry => {
                  const oppVal = parseFloat(entry.calculated_opportunity_value || entry.opportunity_value || 0);
                  return (
                    <Box key={entry.id} bg="white" border="1px" borderColor="gray.200" borderRadius="md"
                      p={3} boxShadow="sm" _hover={{ boxShadow: 'md', borderColor: stage.color || 'blue.300' }}
                      transition="all 0.15s" opacity={saving === entry.id ? 0.6 : 1}>
                      {/* Company name / logo */}
                      <Flex justify="space-between" align="start" mb={1.5}>
                        <HStack spacing={2}>
                          {entry.logo_url ? (
                            <Image src={`/api/files/${entry.logo_url}`} w="28px" h="28px" objectFit="contain" borderRadius="sm" />
                          ) : (
                            <Box w="28px" h="28px" bg="gray.100" borderRadius="sm" display="flex" alignItems="center" justifyContent="center">
                              <Text fontSize="10px" fontWeight="700" color="gray.500">{(entry.company_name || '?')[0]}</Text>
                            </Box>
                          )}
                          <Box>
                            <Text fontSize="12px" fontWeight="700" lineHeight="1.2" noOfLines={1}>
                              {entry.company_name || '(no company)'}
                            </Text>
                            {entry.contact_first_name && (
                              <Text fontSize="9px" color="gray.500">{entry.contact_first_name} {entry.contact_last_name || ''}</Text>
                            )}
                          </Box>
                        </HStack>
                      </Flex>

                      {/* Opportunity value */}
                      {oppVal > 0 && (
                        <Text fontSize="14px" fontWeight="800" color="green.600" mb={1}>{fmtMoney(oppVal)}</Text>
                      )}

                      {/* Campaign */}
                      {entry.campaign_name && (
                        <Badge fontSize="8px" colorScheme="purple" variant="subtle" mb={1}>{entry.campaign_name}</Badge>
                      )}

                      {/* Conversion likelihood slider */}
                      <Box mt={1}>
                        <Flex justify="space-between" align="center" mb={0.5}>
                          <Text fontSize="9px" color="gray.400" fontWeight="600">CONVERSION</Text>
                          <Text fontSize="10px" fontWeight="700" color={
                            (entry.conversion_likelihood || 0) >= 70 ? 'green.600' :
                            (entry.conversion_likelihood || 0) >= 40 ? 'orange.500' : 'gray.500'
                          }>{entry.conversion_likelihood || 0}%</Text>
                        </Flex>
                        <Slider size="sm" min={0} max={100} step={5}
                          value={entry.conversion_likelihood || 0}
                          onChange={(v) => updateEntry(entry.id, 'conversion_likelihood', v)}
                          colorScheme={
                            (entry.conversion_likelihood || 0) >= 70 ? 'green' :
                            (entry.conversion_likelihood || 0) >= 40 ? 'orange' : 'gray'
                          }>
                          <SliderTrack h="4px"><SliderFilledTrack /></SliderTrack>
                          <SliderThumb boxSize={3} />
                        </Slider>
                      </Box>

                      {/* Dates */}
                      <HStack mt={1.5} spacing={2} fontSize="9px" color="gray.400">
                        {entry.last_outreach_date && <Text>Out: {new Date(entry.last_outreach_date).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}</Text>}
                        {entry.last_response_date && <Text>In: {new Date(entry.last_response_date).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}</Text>}
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          );
        })}
      </Flex>
    </VStack>
  );

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Business Pipeline">
      {content}
    </PageShell>
  );
}
